import axios from "axios";

/**
 * Base API URL
 * - Local: http://localhost:5050/api
 * - Prod: set via environment variable
 */
const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:5050/api";

console.log("🌐 API Base URL:", API_BASE);

/**
 * Axios instance
 */
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

/**
 * Attach auth token automatically
 */
api.interceptors.request.use(
  (config) => {
    console.log("📤 Outgoing request:", config.method?.toUpperCase(), config.url);
    
    // Try accessToken first (new), then fall back to token (old)
    const accessToken = localStorage.getItem("accessToken");
    const oldToken = localStorage.getItem("token");
    
    console.log("🔑 Tokens in localStorage:", {
      accessToken: accessToken ? `${accessToken.substring(0, 20)}...` : "NOT FOUND",
      token: oldToken ? `${oldToken.substring(0, 20)}...` : "NOT FOUND"
    });
    
    const token = accessToken || oldToken;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log("✅ Authorization header set");
    } else {
      console.warn("⚠️ NO TOKEN FOUND - Request will fail if auth is required");
    }
    
    return config;
  },
  (error) => {
    console.error("❌ Request interceptor error:", error);
    return Promise.reject(error);
  }
);

/**
 * Handle 401 errors (token expired/invalid)
 */
api.interceptors.response.use(
  (response) => {
    // console.log("✅ Response received:", response.config.url, "Status:", response.status);
    return response;
  },
  (error) => {
    console.error("❌ Response error:", {
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });
    
    if (error.response?.status === 401) {
      console.log("🚫 401 Unauthorized - Clearing tokens and redirecting to login");
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

/**
 * --------------------
 * Auth
 * --------------------
 */
export const login = async ({ email, password, otp }) => {
  console.log("🔐 Login attempt:", { email, hasPassword: !!password, hasOtp: !!otp });
  
  const res = await api.post("/login", { email, password, otp });
  
  console.log("📥 Login response:", {
    success: res.data.success,
    hasAccessToken: !!res.data.accessToken,
    hasRefreshToken: !!res.data.refreshToken,
    hasToken: !!res.data.token,
    twoFactorRequired: res.data.twoFactorRequired,
    setupRequired: res.data.setupRequired
  });
  
  // Store tokens from response
  if (res.data.accessToken) {
    localStorage.setItem("accessToken", res.data.accessToken);
    localStorage.setItem("token", res.data.accessToken); // Backward compatibility
    console.log("💾 Stored accessToken in localStorage");
  }
  if (res.data.refreshToken) {
    localStorage.setItem("refreshToken", res.data.refreshToken);
    console.log("💾 Stored refreshToken in localStorage");
  }
  
  return res.data;
};

/**
 * Logout - clear tokens and redirect
 */
export const logout = () => {
  console.log("👋 Logging out - clearing tokens");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("token");
  window.location.href = "/login";
};

/**
 * --------------------
 * Dashboard
 * --------------------
 */
export const getDashboard = async (fundCode = "ALL") => {
  // console.log("📊 Fetching dashboard summary for fund:", fundCode);
  const res = await api.get("/dashboard/summary", {
    params: { fund_code: fundCode },
  });
  // console.log("📊 Dashboard summary received:", res.data);
  return res.data;
};

export const getDashboardTrades = async (fund = "ALL") => {
  console.log("📈 Fetching dashboard trades for fund:", fund);
  try {
    const res = await api.get("/dashboard/trades-chart", {
      params: { fund },
    });
    // console.log("📈 Dashboard trades received:", res.data);
    console.log("📈 Dashboard trades received:");
    return res.data;
  } catch (err) {
    console.error("❌ Failed to fetch dashboard trades", err);
    throw err;
  }
};